/**
 * informe-detalle-helpers.ts — pure helpers for the InformeDetalle page.
 *
 * Extracted for testability (TDD, strict-tdd.md Extract-Before-Mock rule).
 *
 * DWN-01: resolveDownloadSource ensures the online "Descargar PDF" button always
 * uses the client-generated blob (ourPdfUrl), never the server Puppeteer endpoint.
 */

import type { EstadoInforme, Informe, TipoInforme } from "../types.js";
import { resolveDisplayEstado } from "../../technician-panel/components/estado-display.js";

// ── Fase 2b: pin state machine for "Guardar para offline" button ──────────────

/**
 * PinState — state of the "Guardar para offline" button (§3.3).
 *
 * - hidden:          no PDF source at all (no server URL and no client blob) — button not shown.
 * - downloading:     user clicked; PDF bytes are being fetched and saved to IDB.
 * - saved:           PDF is pinned in IDB (immutable to LRU eviction).
 * - offline-no-cache: offline, no local bytes available; button shown but disabled.
 * - not-saved:       PDF source exists and is not pinned; button enabled.
 */
export type PinState = "hidden" | "downloading" | "saved" | "offline-no-cache" | "not-saved";

export interface ComputePinStateArgs {
  /** True when the report has a server-generated PDF URL (pdfUrl != null). */
  hasPdfUrl: boolean;
  /** True when the on-device client-generated PDF (pdfme) is ready. */
  hasClientPdf: boolean;
  /** True when a PDF blob is already cached in IDB (from a prior visit). */
  hasCachedBlob: boolean;
  /** True when the PDF is currently pinned in IDB. */
  isPinned: boolean;
  /** True when a pin/save operation is in progress. */
  isDownloading: boolean;
  /** True when the device has network connectivity. */
  online: boolean;
}

/**
 * Computes the "Guardar para offline" button state (§3.3).
 *
 * Pure function — all arguments are booleans so the component can memoize or
 * re-compute cheaply on every state change.
 *
 * Decision tree:
 * 1. No PDF source at all (no bytes anywhere) → hidden.
 * 2. Already pinned → saved.
 * 3. Download in progress → downloading.
 * 4. Offline with no local bytes (no client PDF and no cached blob) → offline-no-cache.
 * 5. Everything else → not-saved (can be saved).
 */
export function computePinState({
  hasPdfUrl,
  hasClientPdf,
  hasCachedBlob,
  isPinned,
  isDownloading,
  online,
}: ComputePinStateArgs): PinState {
  const hasAnySource = hasPdfUrl || hasClientPdf || hasCachedBlob;
  if (!hasAnySource) return "hidden";
  if (isPinned) return "saved";
  if (isDownloading) return "downloading";
  // Offline with no local bytes available to save → disabled
  const canSaveOffline = hasClientPdf || hasCachedBlob;
  if (!online && !canSaveOffline) return "offline-no-cache";
  return "not-saved";
}

// ── Fase 2b: "Ver formulario" online gate (§3.2) ────────────────────────────

/**
 * Returns true when the "Ver formulario" (read-only form view) action is available.
 *
 * §3.2: "Ver formulario" (payload, consulta) = offline NOT available. The read-only
 * form view requires the full payload from the server; we intentionally do not mirror
 * payload for every report (only the technician's own work is kept locally).
 *
 * NOTE: "Editar / continuar MI informe" (editor path) is separate from this — the
 * editor is accessible from TechnicianPanel via the "Continuar" button on borrador
 * rows, not from this detail page. This helper gates only the CONSULT path here.
 *
 * ponytail: pure predicate. If distinguishing "own-editable offline" from "consult-only"
 * is needed in the future, extend with an `isOwnReport` flag (see §3.2 note in plan).
 */
export function canVerFormulario(online: boolean): boolean {
  return online;
}

// ── Auto-generate PDF ─────────────────────────────────────────────────────────

/**
 * Returns true only when the detail page should automatically trigger PDF generation on mount.
 *
 * Conditions: pdfUrl must be absent AND estado must be sincronizado or enviado (server-side
 * generation available). en_cola reports are offline/unsynced — generation is not possible yet.
 *
 * ponytail: pure predicate, no side effects.
 */
export function shouldAutoGeneratePdf(estado: EstadoInforme, pdfUrl: string | null): boolean {
  if (pdfUrl !== null) return false;
  return estado === "sincronizado" || estado === "enviado";
}

/**
 * Returns true when the "Enviar" action should be visible on the detail page.
 * Only sincronizado reports can be sent (marks as enviado → read-only).
 *
 * ponytail: pure predicate.
 */
export function canEnviar(estado: EstadoInforme): boolean {
  return estado === "sincronizado";
}

/**
 * Returns true when the "Abrir correo con PDF adjunto" (compartir) action is available.
 * Requires a non-null pdfUrl — sharing requires an actual PDF to attach or link.
 *
 * This is INDEPENDENT of canEnviar / marcar-enviado. The technician can:
 *  - Share without marking as sent.
 *  - Mark as sent without sharing.
 *  - Do both, in any order.
 *
 * ponytail: pure predicate, no side effects.
 */
export function canCompartir(pdfUrl: string | null): boolean {
  return pdfUrl !== null;
}

/**
 * Returns which child tipos can be created from a given (tipo, estado).
 * The gate mirrors TechnicianPanel: only sincronizado/enviado allow continuation.
 *
 * IE → [IS, AC]
 * IS → [AC]
 * AC → []  (terminal)
 */
export function nextFlowOptions(tipo: TipoInforme, estado: EstadoInforme): TipoInforme[] {
  if (estado !== "sincronizado" && estado !== "enviado") return [];
  if (tipo === "IE") return ["IS", "AC"];
  if (tipo === "IS") return ["AC"];
  return []; // AC is terminal
}

/**
 * True when an Informe has no real server-assigned número yet (local borrador /
 * pending sync). These are the only cases where a preview código should be shown.
 *
 * ponytail: pure predicate. Matches the placeholder strings written by the
 * local-first load paths ("(pendiente)", "(sin número)") plus empty.
 */
export function isPendingNumero(numero: string): boolean {
  const n = numero.trim();
  return n === "" || n === "(pendiente)" || n === "(sin número)";
}

/** Shape returned by buildInformeDetalle. */
export interface InformeDetalleDisplay {
  tipo: TipoInforme;
  numero: string;
  cliente: string;
  tecnico: string;
  estadoLabel: string;
  fechaInicio: string;
  fechaCierre: string;
  emision: string;
}

/**
 * Resolves the URL to use for the "Descargar PDF" / "Ver PDF" actions (DWN-01).
 *
 * The online download MUST always serve the client-generated blob (`ourPdfUrl`),
 * not the server-side Puppeteer endpoint (`serverPdfUrl`). Preview === download
 * by construction. The server URL is retained only for email share (compartirPdf).
 *
 * Returns:
 *  - `ourPdfUrl` when the client blob is ready (regardless of serverPdfUrl).
 *  - `null` when the client blob is not yet generated.
 *
 * ponytail: pure mapping, no side effects.
 */
export function resolveDownloadSource(
  _serverPdfUrl: string | null,
  ourPdfUrl: string | null,
): string | null {
  return ourPdfUrl;
}

/**
 * Maps an Informe + local-only flag to display fields for the detail header.
 * Uses resolveDisplayEstado so offline en_cola shows "Pendiente".
 *
 * ponytail: pure mapping, no side effects.
 */
export function buildInformeDetalle(
  inf: Informe,
  isLocalOnly: boolean,
  numeroPreview: string | null = null,
): InformeDetalleDisplay {
  const dg = (inf.payload as Record<string, unknown>)?.datosGenerales as
    | Record<string, string>
    | undefined;

  // Show the tentative preview código only while the real número is pending.
  const numero = isPendingNumero(inf.numero) && numeroPreview ? numeroPreview : inf.numero;

  return {
    tipo: inf.tipo,
    numero,
    cliente: inf.cliente,
    tecnico: inf.autorNombre ?? "—",
    estadoLabel: resolveDisplayEstado(inf.estado, isLocalOnly),
    fechaInicio: dg?.fechaInicio ?? "",
    fechaCierre: dg?.fechaCierre ?? "",
    emision: dg?.emision ?? "",
  };
}
